import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, X, ShieldCheck, Boxes, Beaker, Radio } from 'lucide-react';
import { LAYERS, GATES, STAGES } from '../mock';
import FlightPathViz from '../components/FlightPathViz';
import ClosingCTA from '../components/ClosingCTA';
import SEO from '../components/SEO';

const layerIcon = (id) => ({
  L1: <Beaker size={22} />, L2: <Boxes size={22} />, L3: <ShieldCheck size={22} />, L4: <Radio size={22} />,
}[id]);

// Subtle grid overlay for the hero — hand-drafted, Cream/Ink token.
function GridOverlay() {
  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden="true"
      preserveAspectRatio="none"
      viewBox="0 0 1200 500"
    >
      <defs>
        <pattern id="nw-grid" width="60" height="60" patternUnits="userSpaceOnUse">
          <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#1A1A1A" strokeOpacity="0.06" strokeWidth="1" />
        </pattern>
        <linearGradient id="nw-fade" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#FBFAF6" stopOpacity="0" />
          <stop offset="30%" stopColor="#FBFAF6" stopOpacity="1" />
          <stop offset="70%" stopColor="#FBFAF6" stopOpacity="1" />
          <stop offset="100%" stopColor="#FBFAF6" stopOpacity="0" />
        </linearGradient>
        <mask id="nw-mask">
          <rect width="1200" height="500" fill="url(#nw-fade)" />
        </mask>
      </defs>
      <rect width="1200" height="500" fill="url(#nw-grid)" mask="url(#nw-mask)" />
    </svg>
  );
}

// Vibrant framework signet — animated four-layer diagram for the hero right column.
function LayerSignet() {
  return (
    <div className="relative w-full aspect-square max-w-[340px] mx-auto">
      <svg viewBox="0 0 260 260" className="w-full h-full">
        <defs>
          <radialGradient id="core" r="60%">
            <stop offset="0%" stopColor="#22A94F" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#22A94F" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Concentric layer rings */}
        {[110, 82, 56, 32].map((r, i) => (
          <motion.circle
            key={r}
            cx="130" cy="130" r={r}
            fill="none"
            stroke={i === 3 ? '#22A94F' : '#0F3D22'}
            strokeOpacity={0.18 + i * 0.14}
            strokeWidth={1.4}
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 1.4, delay: i * 0.18, ease: 'easeInOut' }}
          />
        ))}

        {/* Layer tick marks */}
        {['L1', 'L2', 'L3', 'L4'].map((id, i) => {
          const r = [110, 82, 56, 32][i];
          const angle = -Math.PI / 2 + i * 0.22;
          const x = 130 + Math.cos(angle) * r;
          const y = 130 + Math.sin(angle) * r;
          return (
            <motion.g
              key={id}
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 + i * 0.18, duration: 0.4 }}
            >
              <circle cx={x} cy={y} r={5} fill="#FBFAF6" stroke="#0F3D22" strokeWidth={1.5} />
              <text x={x + 10} y={y + 3} style={{ fontSize: 10, fill: '#0F3D22', fontFamily: 'JetBrains Mono, monospace', letterSpacing: 1, fontWeight: 600 }}>{id}</text>
            </motion.g>
          );
        })}

        {/* Core */}
        <circle cx="130" cy="130" r="55" fill="url(#core)" />
        <motion.circle
          cx="130" cy="130" r="14"
          fill="#0F3D22"
          animate={{ scale: [1, 1.15, 1] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
        />
        <text x="130" y="134" textAnchor="middle" style={{ fontSize: 9, fill: '#22A94F', fontFamily: 'JetBrains Mono, monospace', letterSpacing: 1, fontWeight: 700 }}>NW</text>

        {/* Orbiting release ticks */}
        {[0, 1, 2].map((i) => (
          <motion.g
            key={`orb-${i}`}
            style={{ originX: '130px', originY: '130px' }}
            animate={{ rotate: 360 }}
            transition={{ duration: 12 + i * 4, repeat: Infinity, ease: 'linear' }}
          >
            <circle cx={130 + 110} cy={130} r={3} fill="#22A94F" />
          </motion.g>
        ))}
      </svg>
      <div className="absolute inset-x-0 -bottom-2 text-center mono text-[10px] tracking-[0.24em] uppercase text-[#178838]">
        Four layers · always present
      </div>
    </div>
  );
}

const layerColorMap = {
  L1: { accent: '#22A94F', halo: 'rgba(34,169,79,0.12)' },
  L2: { accent: '#5B7BB8', halo: 'rgba(91,123,184,0.14)' },
  L3: { accent: '#8B5CF6', halo: 'rgba(139,92,246,0.12)' },
  L4: { accent: '#E6A83A', halo: 'rgba(230,168,58,0.14)' },
};

export default function NeuralWorks() {
  return (
    <div className="bg-[#FBFAF6]">
      <SEO
        title="NeuralWorks — the framework we build with"
        description="NeuralWorks is DigitalWorks' framework for AI-native delivery: reviewed increments, engineering gates and governance made operational — so agent-assisted delivery reaches production safely."
        canonical="https://digitalworks.co/neuralworks"
      />
      {/* HERO — vibrant, cream, with animated signet */}
      <section className="relative pt-16 pb-16 overflow-hidden">
        <GridOverlay />
        <div className="dwx relative">
          <div className="relative h-[2px] w-[220px] bg-[#22A94F] mb-8" />

          <div className="grid grid-cols-12 gap-8 items-start">
            <div className="col-span-12 lg:col-span-8">
              <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#22A94F] rise">The framework</div>
              <h1 className="serif-tight text-[64px] md:text-[110px] leading-[0.92] mt-4 text-[#1A1A1A] rise" style={{ animationDelay: '60ms' }}>
                Neural<span className="text-[#22A94F]">Works</span>.
              </h1>
              <h2 className="serif-tight text-[36px] md:text-[52px] leading-[0.98] text-[#1A1A1A]/60 mt-2 max-w-3xl rise" style={{ animationDelay: '120ms' }}>
                Delivery &mdash; just like a flight plan.
              </h2>

              <div className="mt-10 grid grid-cols-12 gap-6">
                <div className="col-span-12 md:col-span-8 text-[17px] leading-relaxed text-[#1A1A1A]/80 rise" style={{ animationDelay: '180ms' }}>
                  <p>Aviation is the safest way to travel and the fastest. It became both at once, and not by accident. Flight plans, air traffic control and regulation grew alongside the speed, and the speed is possible because of them.</p>
                  <p className="mt-5 text-[#1A1A1A] font-medium">All acceleration is risky &mdash; the greater the risk, the greater the planning and protocols required.</p>
                  <p className="mt-5">NeuralWorks is our answer to that for AI delivery. Four layers, always present. Five stages, travelled at your pace.</p>
                </div>
              </div>
            </div>

            <div className="col-span-12 lg:col-span-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
              >
                <LayerSignet />
              </motion.div>

              <div className="mt-8 rounded-[20px] bg-[#0F3D22] p-6 text-[#FBFAF6]">
                <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#7ED4A0] mb-4">Explore</div>
                <ul className="space-y-3 text-[14px]">
                  <li><Link to="/neuralworks/how-it-works" className="inline-flex items-center gap-2 text-[#FBFAF6] hover:text-[#22A94F] transition-colors"><ArrowRight size={14} className="opacity-70" />How it works &mdash; Tests as Spec</Link></li>
                  <li><Link to="/neuralworks/accelerators" className="inline-flex items-center gap-2 text-[#FBFAF6] hover:text-[#22A94F] transition-colors"><ArrowRight size={14} className="opacity-70" />Accelerators &mdash; named components</Link></li>
                  <li><Link to="/neuralworks/security-and-governance" className="inline-flex items-center gap-2 text-[#FBFAF6] hover:text-[#22A94F] transition-colors"><ArrowRight size={14} className="opacity-70" />Security &amp; Governance</Link></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FLIGHT PATH VIZ — kept, wrapped in soft band */}
      <section className="py-16 bg-[#F4F2EC] border-y border-[#E8E4D9]">
        <div className="dwx">
          <FlightPathViz variant="full" />
        </div>
      </section>

      {/* FOUR LAYERS — dark theatrical band, each layer with its own accent tint */}
      <section className="py-24 bg-[#1A1A1A] text-[#FBFAF6] relative overflow-hidden">
        {/* subtle brand-green ambient light */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full"
          style={{ background: 'radial-gradient(closest-side, rgba(34,169,79,0.14), transparent)' }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full"
          style={{ background: 'radial-gradient(closest-side, rgba(126,212,160,0.10), transparent)' }}
        />

        <div className="dwx relative">
          <div className="grid grid-cols-12 gap-8 mb-14">
            <div className="col-span-12 md:col-span-7">
              <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#22A94F] mb-6">The four layers</div>
              <h2 className="serif-tight text-[46px] md:text-[72px] leading-[0.95]">
                One system.<br />
                <span className="text-[#FBFAF6]/55">Four layers. Always present.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-4 md:col-start-9 flex items-end">
              <p className="text-[15px] leading-relaxed text-[#FBFAF6]/70">Nothing here is optional. Every engagement inherits all four layers &mdash; and the review discipline that keeps them honest.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {LAYERS.map((l, i) => {
              const col = layerColorMap[l.id];
              return (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="group rounded-[24px] bg-[#2B2B2B] border border-[#FBFAF6]/8 p-8 relative overflow-hidden transition-transform hover:-translate-y-0.5"
                >
                  <div
                    aria-hidden="true"
                    className="absolute -top-16 -right-16 w-[200px] h-[200px] rounded-full transition-opacity opacity-70 group-hover:opacity-100"
                    style={{ background: `radial-gradient(closest-side, ${col.halo}, transparent)` }}
                  />
                  <div className="relative flex items-start justify-between">
                    <div className="flex items-center gap-3" style={{ color: col.accent }}>
                      {layerIcon(l.id)}
                      <span className="mono text-[11px] tracking-[0.22em] uppercase">{l.id}</span>
                    </div>
                    <span
                      className="mono text-[10px] tracking-[0.22em] uppercase px-2.5 py-1 rounded-full border"
                      style={{ color: col.accent, borderColor: `${col.accent}55`, backgroundColor: `${col.accent}12` }}
                    >
                      {l.metaphor}
                    </span>
                  </div>
                  <div className="relative serif text-[32px] mt-5 leading-tight">{l.functionalName}</div>
                  <div className="relative mt-4 text-[15px] leading-relaxed text-[#FBFAF6]/78">{l.summary}</div>
                  <div className="relative mt-6 flex flex-wrap gap-2">
                    {l.components.map((c) => (
                      <span key={c} className="mono text-[11px] px-2.5 py-1.5 rounded-full bg-[#FBFAF6]/6 border border-[#FBFAF6]/12 text-[#FBFAF6]/75">{c}</span>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROPRIETARY BOUNDARY */}
      <section className="py-24">
        <div className="dwx">
          <div className="grid grid-cols-12 gap-8 mb-12">
            <div className="col-span-12 md:col-span-7">
              <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#22A94F] mb-4">The proprietary boundary</div>
              <h2 className="serif-tight text-[46px] md:text-[68px] leading-[0.95] text-[#1A1A1A]">
                Anyone can buy the tools.
                <br />
                <span className="text-[#1A1A1A]/50">This is the system that makes them safe to run.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-5 md:col-start-8 flex items-end">
              <p className="text-[15px] leading-relaxed text-[#1A1A1A]/75">We did not invent the coding agents. What is ours is the specification method, the containment architecture, the risk taxonomy, the execution ledger, the privacy architecture, and the sequence for adopting all of it without stopping delivery.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="rounded-[24px] bg-[#F4F2EC] border border-[#E8E4D9] p-9"
            >
              <div className="flex items-center justify-between mb-8">
                <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#1A1A1A]/60">Assembled &mdash; not proprietary</div>
                <div className="w-9 h-9 rounded-full bg-[#A63D2F]/12 flex items-center justify-center text-[#A63D2F]"><X size={16} /></div>
              </div>
              <ul className="space-y-3.5 text-[14px]">
                {['Frontier coding agents (Claude Code, alternatives)','Keycloak, OIDC','K3S, CDC tooling, CI/CD platforms','Public benchmarks (SWE-bench, AA-Omniscience)','Cloud infrastructure'].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-[#1A1A1A]/80">
                    <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#A63D2F] flex-shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="rounded-[24px] bg-[#0F3D22] p-9 text-[#FBFAF6] relative overflow-hidden"
            >
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-24 -right-24 w-[280px] h-[280px] rounded-full"
                style={{ background: 'radial-gradient(closest-side, rgba(34,169,79,0.30), transparent)' }}
              />
              <div className="relative flex items-center justify-between mb-8">
                <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#7ED4A0]">Proprietary &mdash; DigitalWorks IP</div>
                <div className="w-9 h-9 rounded-full bg-[#22A94F]/30 flex items-center justify-center text-[#7ED4A0]"><Check size={16} /></div>
              </div>
              <ul className="relative space-y-3.5 text-[14px]">
                {['Tests-as-Spec methodology and the 8-step workflow','Auth Broker Service — on-demand, time-limited access','Shadow Data Service — CDC + PII stripping','Governance model: risk taxonomy + four-tier gate system','Graph Execution Ledger — proposal vs. execution logging','Privacy architecture: pseudonym layer + single-trigger erasure','Cloud-agnostic residency deployment model','The five-stage adoption sequence'].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-[#FBFAF6]/92">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#22A94F] flex-shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* GATES — vibrant panel */}
      <section className="py-24 bg-[#F4F2EC] border-y border-[#E8E4D9]">
        <div className="dwx">
          <div className="grid grid-cols-12 gap-8 mb-12">
            <div className="col-span-12 md:col-span-6">
              <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#22A94F] mb-4">Governance</div>
              <h2 className="serif-tight text-[46px] md:text-[68px] leading-[0.95] text-[#1A1A1A]">
                Four gates.
                <br />
                <span className="text-[#22A94F]">One rule.</span>
              </h2>
              <p className="mt-6 text-[16px] leading-relaxed text-[#1A1A1A]/75 max-w-lg">Agents propose and prepare. Two different people review and release. Nothing reaches your production environment on an agent&rsquo;s authority.</p>
              <Link to="/neuralworks/security-and-governance" className="mt-8 inline-flex items-center gap-2 text-[14px] font-semibold text-[#178838] hover:text-[#22A94F] transition-colors">
                See the full governance model <ArrowRight size={16} />
              </Link>
            </div>
            <div className="col-span-12 md:col-span-6 grid grid-cols-2 gap-3">
              {GATES.map((g, i) => {
                const map = { green: '#22A94F', caution: '#B5822A', indigo: '#5B4FCF', block: '#A63D2F' };
                const tone = map[g.color];
                return (
                  <motion.div
                    key={g.tier}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                    className="rounded-[18px] bg-[#FBFAF6] p-5 border border-[#1A1A1A]/8 relative overflow-hidden group"
                  >
                    <div
                      aria-hidden="true"
                      className="absolute inset-x-0 top-0 h-1"
                      style={{ backgroundColor: tone }}
                    />
                    <div className="mono text-[11px] tracking-[0.22em] uppercase" style={{ color: tone }}>Tier {g.tier}</div>
                    <div className="serif text-[22px] mt-2 leading-tight text-[#1A1A1A]">{g.name}</div>
                    <div className="mt-2 text-[13px] leading-relaxed text-[#1A1A1A]/70">{g.behaviour}</div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* LIMITS — quiet, confident */}
      <section className="py-24">
        <div className="dwx max-w-4xl">
          <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#22A94F]">Limits</div>
          <h2 className="serif-tight text-[46px] md:text-[68px] leading-[0.95] mt-4 text-[#1A1A1A]">What NeuralWorks does <span className="text-[#22A94F]">not</span> do.</h2>
          <div className="mt-10 space-y-5">
            {[
              'It does not remove the need for engineers.',
              'It does not work on an estate with no test coverage until that coverage exists.',
              'It does not let an agent release to production.',
              'It does not make an unclear requirement clear — it makes the unclarity visible before code is written, which is a different and more useful thing.',
            ].map((line, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="flex items-start gap-4 text-[17px] leading-relaxed text-[#1A1A1A]/85 border-l-2 border-[#22A94F] pl-5"
              >
                {line}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ASCENT PREVIEW */}
      <section className="py-24 bg-[#1A1A1A] text-[#FBFAF6]">
        <div className="dwx">
          <div className="grid grid-cols-12 gap-8 mb-12">
            <div className="col-span-12 md:col-span-7">
              <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#22A94F] mb-4">The ascent</div>
              <h2 className="serif-tight text-[46px] md:text-[68px] leading-[0.95]">
                Five stages.
                <br />
                <span className="text-[#FBFAF6]/55">You choose how far.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-4 md:col-start-9 flex items-end">
              <p className="text-[14px] leading-relaxed text-[#FBFAF6]/70">Stages 01&ndash;02 are running today. Stages 03&ndash;05 are the roadmap frontier &mdash; defined, and being built with clients rather than ahead of them.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {STAGES.map((s, i) => {
              const isLive = s.status === 'operational';
              return (
                <motion.div
                  key={s.stage}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className={`rounded-[18px] p-5 border transition-colors ${isLive ? 'bg-[#2B2B2B] border-[#22A94F]/40' : 'bg-transparent border-dashed border-[#FBFAF6]/25'}`}
                >
                  <div className="mono text-[11px] tracking-[0.22em] uppercase flex items-center gap-2" style={{ color: isLive ? '#22A94F' : '#FBFAF6' }}>
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${isLive ? 'bg-[#22A94F]' : 'bg-[#FBFAF6]/40'} ${isLive ? 'pulse-soft' : ''}`} />
                    Stage {s.stage}
                  </div>
                  <div className="serif text-[19px] mt-3 leading-tight">{s.name}</div>
                  <div className="mono text-[10px] mt-2" style={{ color: isLive ? '#7ED4A0' : 'rgba(251,250,246,0.45)' }}>{s.horizon}</div>
                  <div className={`mt-3 text-[12.5px] leading-relaxed ${isLive ? 'text-[#FBFAF6]/72' : 'text-[#FBFAF6]/45'}`}>{s.summary}</div>
                </motion.div>
              );
            })}
          </div>

          <Link to="/approach" className="mt-10 inline-flex items-center gap-2 text-[15px] font-medium text-[#22A94F] hover:text-[#7ED4A0] transition-colors">
            See the full ascent <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <ClosingCTA />
    </div>
  );
}
